// chessEngine.js
// Pure chess rules/state helpers. No React dependencies.

export const INITIAL_BOARD = [
  ["bR", "bN", "bB", "bQ", "bK", "bB", "bN", "bR"],
  ["bP", "bP", "bP", "bP", "bP", "bP", "bP", "bP"],
  ["", "", "", "", "", "", "", ""],
  ["", "", "", "", "", "", "", ""],
  ["", "", "", "", "", "", "", ""],
  ["", "", "", "", "", "", "", ""],
  ["wP", "wP", "wP", "wP", "wP", "wP", "wP", "wP"],
  ["wR", "wN", "wB", "wQ", "wK", "wB", "wN", "wR"],
];

export const INITIAL_CASTLING = {
  wK: true,
  wQ: true,
  bK: true,
  bQ: true,
};

export function createInitialBoard() {
  return INITIAL_BOARD.map((row) => [...row]);
}

export function createInitialGameState() {
  return {
    turn: "w",
    castling: { ...INITIAL_CASTLING },
    enPassant: null,
    halfmove: 0,
    fullmove: 1,
  };
}

export function cloneBoard(board) {
  return board.map((row) => [...row]);
}

export function inBounds(r, c) {
  return r >= 0 && r < 8 && c >= 0 && c < 8;
}

export function opposite(color) {
  return color === "w" ? "b" : "w";
}

export function coord(r, c) {
  return `${String.fromCharCode(97 + c)}${8 - r}`;
}

export function findKing(board, color) {
  for (let r = 0; r < 8; r += 1) {
    for (let c = 0; c < 8; c += 1) {
      if (board[r][c] === `${color}K`) {
        return [r, c];
      }
    }
  }

  return null;
}

export function isSquareAttacked(board, r, c, byColor) {
  const pawnDir = byColor === "w" ? -1 : 1;

  for (const dc of [-1, 1]) {
    const pr = r - pawnDir;
    const pc = c - dc;

    if (
      inBounds(pr, pc) &&
      board[pr][pc] === `${byColor}P`
    ) {
      return true;
    }
  }

  const knightMoves = [
    [2, 1],
    [2, -1],
    [-2, 1],
    [-2, -1],
    [1, 2],
    [1, -2],
    [-1, 2],
    [-1, -2],
  ];

  for (const [dr, dc] of knightMoves) {
    const nr = r + dr;
    const nc = c + dc;

    if (
      inBounds(nr, nc) &&
      board[nr][nc] === `${byColor}N`
    ) {
      return true;
    }
  }

  const kingMoves = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ];

  for (const [dr, dc] of kingMoves) {
    const nr = r + dr;
    const nc = c + dc;

    if (
      inBounds(nr, nc) &&
      board[nr][nc] === `${byColor}K`
    ) {
      return true;
    }
  }

  const rays = [
    [1, 0, ["R", "Q"]],
    [-1, 0, ["R", "Q"]],
    [0, 1, ["R", "Q"]],
    [0, -1, ["R", "Q"]],
    [1, 1, ["B", "Q"]],
    [1, -1, ["B", "Q"]],
    [-1, 1, ["B", "Q"]],
    [-1, -1, ["B", "Q"]],
  ];

  for (const [dr, dc, types] of rays) {
    let nr = r + dr;
    let nc = c + dc;

    while (inBounds(nr, nc)) {
      const piece = board[nr][nc];

      if (piece) {
        if (
          piece[0] === byColor &&
          types.includes(piece[1])
        ) {
          return true;
        }

        break;
      }

      nr += dr;
      nc += dc;
    }
  }

  return false;
}

export function isInCheck(board, color) {
  const king = findKing(board, color);

  if (!king) {
    return true;
  }

  return isSquareAttacked(
    board,
    king[0],
    king[1],
    opposite(color)
  );
}

export function pseudoMoves(
  board,
  r,
  c,
  state,
  includeCastling = true
) {
  const piece = board[r][c];

  if (!piece) {
    return [];
  }

  const color = piece[0];
  const type = piece[1];
  const moves = [];

  const pushIfOpenOrEnemy = (nr, nc) => {
    if (!inBounds(nr, nc)) {
      return false;
    }

    const target = board[nr][nc];

    if (!target) {
      moves.push({ r: nr, c: nc });
      return true;
    }

    if (
      target[0] !== color &&
      target[1] !== "K"
    ) {
      moves.push({
        r: nr,
        c: nc,
        capture: true,
      });
    }

    return false;
  };

  if (type === "P") {
    const dir = color === "w" ? -1 : 1;
    const startRow = color === "w" ? 6 : 1;
    const promotionRow = color === "w" ? 0 : 7;

    if (
      inBounds(r + dir, c) &&
      !board[r + dir][c]
    ) {
      moves.push({
        r: r + dir,
        c,
        promotion: r + dir === promotionRow,
      });

      if (
        r === startRow &&
        !board[r + 2 * dir][c]
      ) {
        moves.push({
          r: r + 2 * dir,
          c,
          doublePawn: true,
        });
      }
    }

    for (const dc of [-1, 1]) {
      const nr = r + dir;
      const nc = c + dc;

      if (!inBounds(nr, nc)) {
        continue;
      }

      if (
        board[nr][nc] &&
        board[nr][nc][0] !== color &&
        board[nr][nc][1] !== "K"
      ) {
        moves.push({
          r: nr,
          c: nc,
          capture: true,
          promotion: nr === promotionRow,
        });
      } else if (
        state.enPassant &&
        state.enPassant.r === nr &&
        state.enPassant.c === nc
      ) {
        moves.push({
          r: nr,
          c: nc,
          capture: true,
          enPassant: true,
        });
      }
    }

    return moves;
  }

  if (type === "N") {
    const jumps = [
      [2, 1],
      [2, -1],
      [-2, 1],
      [-2, -1],
      [1, 2],
      [1, -2],
      [-1, 2],
      [-1, -2],
    ];

    jumps.forEach(([dr, dc]) =>
      pushIfOpenOrEnemy(r + dr, c + dc)
    );

    return moves;
  }

  if (type === "K") {
    const steps = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, 1],
      [1, -1],
      [-1, 1],
      [-1, -1],
    ];

    steps.forEach(([dr, dc]) =>
      pushIfOpenOrEnemy(r + dr, c + dc)
    );

    if (
      includeCastling &&
      !isInCheck(board, color)
    ) {
      const homeRow = color === "w" ? 7 : 0;

      if (
        r === homeRow &&
        c === 4 &&
        state.castling[`${color}K`] &&
        board[homeRow][7] === `${color}R` &&
        !board[homeRow][5] &&
        !board[homeRow][6] &&
        !isSquareAttacked(
          board,
          homeRow,
          5,
          opposite(color)
        ) &&
        !isSquareAttacked(
          board,
          homeRow,
          6,
          opposite(color)
        )
      ) {
        moves.push({
          r: homeRow,
          c: 6,
          castle: "K",
        });
      }

      if (
        r === homeRow &&
        c === 4 &&
        state.castling[`${color}Q`] &&
        board[homeRow][0] === `${color}R` &&
        !board[homeRow][1] &&
        !board[homeRow][2] &&
        !board[homeRow][3] &&
        !isSquareAttacked(
          board,
          homeRow,
          3,
          opposite(color)
        ) &&
        !isSquareAttacked(
          board,
          homeRow,
          2,
          opposite(color)
        )
      ) {
        moves.push({
          r: homeRow,
          c: 2,
          castle: "Q",
        });
      }
    }

    return moves;
  }

  const dirs =
    type === "B"
      ? [
          [1, 1],
          [1, -1],
          [-1, 1],
          [-1, -1],
        ]
      : type === "R"
      ? [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ]
      : [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
          [1, 1],
          [1, -1],
          [-1, 1],
          [-1, -1],
        ];

  for (const [dr, dc] of dirs) {
    let nr = r + dr;
    let nc = c + dc;

    while (inBounds(nr, nc)) {
      const canContinue =
        pushIfOpenOrEnemy(nr, nc);

      if (!canContinue) {
        break;
      }

      nr += dr;
      nc += dc;
    }
  }

  return moves;
}

export function applyMoveToBoard(
  board,
  from,
  move,
  promotionPiece = "Q"
) {
  const next = cloneBoard(board);
  const piece = next[from.r][from.c];

  next[from.r][from.c] = "";

  if (move.enPassant) {
    const capturedPawnRow =
      piece[0] === "w"
        ? move.r + 1
        : move.r - 1;

    next[capturedPawnRow][move.c] = "";
  }

  if (move.castle === "K") {
    next[move.r][5] = next[move.r][7];
    next[move.r][7] = "";
  } else if (move.castle === "Q") {
    next[move.r][3] = next[move.r][0];
    next[move.r][0] = "";
  }

  next[move.r][move.c] = move.promotion
    ? `${piece[0]}${promotionPiece}`
    : piece;

  return next;
}

export function legalMovesForPiece(
  board,
  r,
  c,
  state
) {
  const piece = board[r][c];

  if (!piece || piece[0] !== state.turn) {
    return [];
  }

  return pseudoMoves(
    board,
    r,
    c,
    state,
    true
  ).filter((move) => {
    const next = applyMoveToBoard(
      board,
      { r, c },
      move,
      "Q"
    );

    return !isInCheck(next, piece[0]);
  });
}

export function allLegalMoves(board, state) {
  const all = [];

  for (let r = 0; r < 8; r += 1) {
    for (let c = 0; c < 8; c += 1) {
      if (board[r][c]?.[0] === state.turn) {
        legalMovesForPiece(
          board,
          r,
          c,
          state
        ).forEach((move) => {
          all.push({
            from: { r, c },
            move,
          });
        });
      }
    }
  }

  return all;
}

export function deriveNextState(
  board,
  state,
  from,
  move
) {
  const piece = board[from.r][from.c];

  const capturedPiece = move.enPassant
    ? `${opposite(piece[0])}P`
    : board[move.r][move.c];

  const nextCastling = {
    ...state.castling,
  };

  if (piece === "wK") {
    nextCastling.wK = false;
    nextCastling.wQ = false;
  }

  if (piece === "bK") {
    nextCastling.bK = false;
    nextCastling.bQ = false;
  }

  if (
    piece === "wR" &&
    from.r === 7 &&
    from.c === 0
  ) {
    nextCastling.wQ = false;
  }

  if (
    piece === "wR" &&
    from.r === 7 &&
    from.c === 7
  ) {
    nextCastling.wK = false;
  }

  if (
    piece === "bR" &&
    from.r === 0 &&
    from.c === 0
  ) {
    nextCastling.bQ = false;
  }

  if (
    piece === "bR" &&
    from.r === 0 &&
    from.c === 7
  ) {
    nextCastling.bK = false;
  }

  if (
    capturedPiece === "wR" &&
    move.r === 7 &&
    move.c === 0
  ) {
    nextCastling.wQ = false;
  }

  if (
    capturedPiece === "wR" &&
    move.r === 7 &&
    move.c === 7
  ) {
    nextCastling.wK = false;
  }

  if (
    capturedPiece === "bR" &&
    move.r === 0 &&
    move.c === 0
  ) {
    nextCastling.bQ = false;
  }

  if (
    capturedPiece === "bR" &&
    move.r === 0 &&
    move.c === 7
  ) {
    nextCastling.bK = false;
  }

  return {
    turn: opposite(state.turn),
    castling: nextCastling,
    enPassant: move.doublePawn
      ? {
          r: (from.r + move.r) / 2,
          c: from.c,
        }
      : null,
    halfmove:
      piece[1] === "P" || capturedPiece
        ? 0
        : state.halfmove + 1,
    fullmove:
      state.fullmove +
      (state.turn === "b" ? 1 : 0),
  };
}

export function positionKey(board, state) {
  const boardString = board
    .flat()
    .map((piece) => piece || "--")
    .join("");

  const castling =
    Object.entries(state.castling)
      .filter(([, allowed]) => allowed)
      .map(([key]) => key)
      .join("") || "-";

  const enPassant = state.enPassant
    ? coord(
        state.enPassant.r,
        state.enPassant.c
      )
    : "-";

  return `${boardString}|${state.turn}|${castling}|${enPassant}`;
}

export function insufficientMaterial(board) {
  const pieces = [];

  for (let r = 0; r < 8; r += 1) {
    for (let c = 0; c < 8; c += 1) {
      if (board[r][c]) {
        pieces.push({
          piece: board[r][c],
          r,
          c,
        });
      }
    }
  }

  const nonKings = pieces.filter(
    ({ piece }) => piece[1] !== "K"
  );

  if (nonKings.length === 0) {
    return true;
  }

  if (
    nonKings.length === 1 &&
    ["B", "N"].includes(
      nonKings[0].piece[1]
    )
  ) {
    return true;
  }

  if (
    nonKings.length === 2 &&
    nonKings.every(
      ({ piece }) => piece[1] === "B"
    )
  ) {
    const squareColors = nonKings.map(
      ({ r, c }) => (r + c) % 2
    );

    return (
      squareColors[0] === squareColors[1]
    );
  }

  return false;
}

function getDisambiguation(
  board,
  state,
  from,
  move,
  piece
) {
  if (
    piece[1] === "P" ||
    piece[1] === "K"
  ) {
    return "";
  }

  const competitors = [];

  for (let r = 0; r < 8; r += 1) {
    for (let c = 0; c < 8; c += 1) {
      if (
        r === from.r &&
        c === from.c
      ) {
        continue;
      }

      if (board[r][c] === piece) {
        const legal = legalMovesForPiece(
          board,
          r,
          c,
          state
        );

        if (
          legal.some(
            (candidate) =>
              candidate.r === move.r &&
              candidate.c === move.c
          )
        ) {
          competitors.push({
            r,
            c,
          });
        }
      }
    }
  }

  if (!competitors.length) {
    return "";
  }

  const sameFile = competitors.some(
    (candidate) =>
      candidate.c === from.c
  );

  const sameRank = competitors.some(
    (candidate) =>
      candidate.r === from.r
  );

  if (!sameFile) {
    return coord(from.r, from.c)[0];
  }

  if (!sameRank) {
    return coord(from.r, from.c)[1];
  }

  return coord(from.r, from.c);
}

export function makeMove(
  board,
  state,
  from,
  move,
  promotionPiece = "Q"
) {
  const piece = board[from.r][from.c];

  const targetPiece = move.enPassant
    ? `${opposite(piece[0])}P`
    : board[move.r][move.c];

  const nextBoard = applyMoveToBoard(
    board,
    from,
    move,
    promotionPiece
  );

  const nextState = deriveNextState(
    board,
    state,
    from,
    move
  );

  let notation = "";

  if (move.castle === "K") {
    notation = "O-O";
  } else if (move.castle === "Q") {
    notation = "O-O-O";
  } else {
    const type =
      piece[1] === "P" ? "" : piece[1];

    const capture = Boolean(
      targetPiece || move.enPassant
    );

    const pawnCapturePrefix =
      piece[1] === "P" && capture
        ? coord(from.r, from.c)[0]
        : "";

    const disambiguation =
      getDisambiguation(
        board,
        state,
        from,
        move,
        piece
      );

    notation =
      `${type}${pawnCapturePrefix}${disambiguation}` +
      `${capture ? "x" : ""}` +
      `${coord(move.r, move.c)}` +
      `${
        move.promotion
          ? `=${promotionPiece}`
          : ""
      }`;
  }

  const opponentState = nextState;
  const opponentMoves = allLegalMoves(
    nextBoard,
    opponentState
  );

  const opponentInCheck = isInCheck(
    nextBoard,
    opponentState.turn
  );

  if (
    opponentInCheck &&
    opponentMoves.length === 0
  ) {
    notation += "#";
  } else if (opponentInCheck) {
    notation += "+";
  }

  return {
    board: nextBoard,
    state: nextState,
    notation,
    capturedPiece: targetPiece || null,
  };
}

export function getGameStatus(
  board,
  state,
  repetitionCount = 1
) {
  const legal = allLegalMoves(
    board,
    state
  );

  const check = isInCheck(
    board,
    state.turn
  );

  if (legal.length === 0) {
    if (check) {
      return {
        over: true,
        reason: "checkmate",
        winner: opposite(state.turn),
        check: true,
        claimable: null,
      };
    }

    return {
      over: true,
      reason: "stalemate",
      winner: null,
      check: false,
      claimable: null,
    };
  }

  if (insufficientMaterial(board)) {
    return {
      over: true,
      reason: "insufficient",
      winner: null,
      check,
      claimable: null,
    };
  }

  // FIDE automatic draw thresholds.
  if (state.halfmove >= 150) {
    return {
      over: true,
      reason: "seventy-five-move",
      winner: null,
      check,
      claimable: null,
    };
  }

  if (repetitionCount >= 5) {
    return {
      over: true,
      reason: "fivefold",
      winner: null,
      check,
      claimable: null,
    };
  }

  return {
    over: false,
    reason: "",
    winner: null,
    check,
    claimable: {
      fiftyMove: state.halfmove >= 100,
      threefold: repetitionCount >= 3,
    },
  };
}
